import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import AuthenticatedShell from 'components/layout/AuthenticatedShell';
import ReviewEnrollmentView from 'components/enrollments/ReviewEnrollmentView';
import { ReviewEnrollment } from 'models/enrollments/ReviewEnrollment';
import type { NodeInstance } from 'models/nodes/NodeInstance';
import { NodeInstanceStatus } from 'models/nodes/NodeInstanceStatus';
import { NodeType } from 'models/nodes/NodeType';
import { ApiService } from 'services/ApiService';

type PageState =
  | { status: 'loading' }
  | { status: 'ready'; enrollment: ReviewEnrollment; nodeInstance: NodeInstance }
  | { status: 'complete' }
  | { status: 'error'; message: string };

const ReviewPage: React.FC = () => {
  const { teamId, enrollmentId } = useParams<{ teamId: string; enrollmentId: string }>();

  const [pageState, setPageState] = useState<PageState>({ status: 'loading' });
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    if (!teamId || !enrollmentId) {
      setPageState({ status: 'error', message: 'Missing required route parameters.' });
      return;
    }

    ApiService.getEnrollmentNodeContext(enrollmentId)
      .then(({ nodeInstanceId, workflowInstanceId }) => {
        const enrollment = new ReviewEnrollment(enrollmentId, teamId);

        // Minimal NodeInstance stub — ReviewEnrollmentView only needs id,
        // workflowInstanceId, and activeEnrollmentId to cast a vote.
        const nodeInstance: NodeInstance = {
          id: nodeInstanceId,
          workflowInstanceId,
          nodeTemplateId: '',
          order: 0,
          status: NodeInstanceStatus.Active,
          activeEnrollmentId: enrollmentId,
          nodeType: NodeType.TeamReview,
          createdAt: '',
        };

        setPageState({ status: 'ready', enrollment, nodeInstance });
      })
      .catch(() => {
        setPageState({
          status: 'error',
          message: 'This review link is no longer active or could not be found.',
        });
      });
  }, [teamId, enrollmentId]);

  const handleReviewComplete = () => {
    setPageState({ status: 'complete' });
  };

  const renderContent = () => {
    switch (pageState.status) {
      case 'loading':
        return (
          <div className="space-y-3" aria-label="Loading review">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
            ))}
          </div>
        );

      case 'error':
        return (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700"
          >
            <p className="font-medium">Review unavailable</p>
            <p className="mt-1 text-red-600">{pageState.message}</p>
          </div>
        );

      case 'complete':
        return (
          <div className="rounded-lg border border-green-200 bg-green-50 px-5 py-6 text-center space-y-1">
            <p className="text-sm font-semibold text-green-800">Review complete</p>
            <p className="text-sm text-green-700">Thank you for casting your vote.</p>
          </div>
        );

      case 'ready':
        return (
          <ReviewEnrollmentView
            enrollment={pageState.enrollment}
            nodeInstance={pageState.nodeInstance}
            onReviewComplete={handleReviewComplete}
          />
        );
    }
  };

  return (
    <AuthenticatedShell>
      <div className="max-w-xl mx-auto px-4 py-10 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Review</h1>
          <p className="text-sm text-gray-500 mt-1">
            Cast your vote for this submission below.
          </p>
        </div>
        {renderContent()}
      </div>
    </AuthenticatedShell>
  );
};

export default ReviewPage;
